/**
 * Project-owner predicates — the single source of truth for the FE "owner"
 * check.
 *
 * There is no first-class FE "owner" role; ownership is derived from the
 * project's creator (`createdBy`). These helpers dedupe the
 * `createdBy === currentUserId` comparison that was previously copy-pasted
 * across Overview, BriefCommentsSidebar, and useProjectMembersModal.
 *
 * Intentionally pure functions (not hooks): the `entities/project` slice may
 * only `import type` from other entity slices, so it can't
 * consume `useCurrentUserId` from `entities/user` as a value. Callers pass the
 * current user id in.
 */

/** Minimal shape both `ProjectDetail` and drafts satisfy. */
interface ProjectOwnerFields {
  createdBy?: string;
}

/**
 * The project's owner id (its creator), or `''` when unknown — e.g. an
 * unpublished draft with no `createdBy` yet. The empty-string fallback matches
 * the previous `project?.createdBy ?? ''` contract in useProjectMembersModal,
 * where an empty owner id simply never matches a member id.
 */
export function getProjectOwnerId(project: ProjectOwnerFields | undefined): string {
  return project?.createdBy ?? '';
}

/**
 * Whether `userId` owns `project` (is its creator). Returns `false` when either
 * the project's `createdBy` or `userId` is missing, so a signed-out user or a
 * draft without a creator never spuriously reads as owner.
 */
export function isProjectOwner(
  project: ProjectOwnerFields | undefined,
  userId: string | undefined,
): boolean {
  return !!project?.createdBy && !!userId && project.createdBy === userId;
}
